import {
  IsArray,
  IsBoolean,
  IsNumber,
  IsOptional,
  IsUUID,
  Max,
  Min,
} from 'class-validator';
import { BaseDto } from '../base.dto';
import { GenderDto, SkinTypeDto } from '../clinical-details';
import { DiagnosisDto } from '../diagnoses';
import { MedicalUnitDto } from '../medical-unit.dtos';
import { ConditionImageDto } from './condition-image.dtos';

export interface ConditionImportDto extends BaseDto {
  age: number;

  gender: GenderDto;

  skipped: boolean;

  diagnosis: DiagnosisDto;

  secondaryDiagnosis?: DiagnosisDto;

  tertiaryDiagnosis?: DiagnosisDto;

  diagnosisType: 'Histopathology' | 'Teledermatology' | 'Clinical';

  clinicalImages: ConditionImageDto[];

  dermoscopicImages: ConditionImageDto[];

  medicalUnit: MedicalUnitDto;

  skinType: SkinTypeDto;
}

export interface ConditionImportSearchResultDto extends BaseDto {
  id: string;
  diagnosis: DiagnosisDto;
  skinType: SkinTypeDto;
  skipped: boolean;
}

export interface ConditionImportSearchRawResult {
  id: string;
  diagnosis_id: string;
  diagnosis_name: string;
  skin_type_id: string;
  skin_type_name: string;
  skipped: boolean;
}

export class ConditionImportSearchDto {
  @IsOptional()
  @IsNumber()
  @Min(0)
  skip?: number;

  @IsOptional()
  @IsNumber()
  @Min(1)
  take?: number;

  @IsOptional()
  @IsArray()
  @IsUUID('all', { each: true })
  diagnosisIds?: string[];

  @IsOptional()
  @IsArray()
  @IsUUID('all', { each: true })
  skinTypeIds?: string[];

  // Filtering to only skipped / not skipped. If null, both are included
  @IsOptional()
  @IsBoolean()
  skipped?: boolean;

  // Optionally exclude id. Used for iterating conditions, ensuring the same condition is not returned
  @IsOptional()
  @IsUUID()
  excludeConditionId?: string;
}

/**
 * DTO for updating a condition for condition import.
 */
export class ConditionImportUpdateDto {
  @IsOptional()
  @IsBoolean()
  importSkipped?: boolean;

  @IsOptional()
  @IsBoolean()
  importDiscarded?: boolean;
}

export class CreateConditionImportDto {
  @IsOptional()
  @IsUUID()
  conditionId?: string;

  @IsOptional()
  @IsUUID()
  /** @deprecated Use conditionId */
  lesionId?: string;

  @IsUUID()
  clinicalImageId: string;

  // If true, a cropped version of clinical image will be uploaded later
  @IsBoolean()
  clinicalImageHasCrop: boolean;

  @IsUUID()
  dermoscopicImageId: string;

  @IsOptional()
  @IsUUID()
  revisedDiagnosisId?: string;

  @IsOptional()
  @IsUUID()
  revisedSecondaryDiagnosisId?: string;

  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(1)
  updatedFocalPointX?: number;

  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(1)
  updatedFocalPointY?: number;

  @IsOptional()
  @IsUUID()
  skinTypeId?: string;

  @IsOptional()
  @IsNumber()
  @Min(0)
  age?: number;
}

/**
 * Result of creating a condition import.
 * Note that training condition id is not included, as we need to be 100% anonymous.
 */
export interface CreateConditionImportResultDto {
  // Id of condition used for import
  conditionId?: string;

  // Id of condition used for import
  /** @deprecated Use conditionId */
  lesionId?: string;

  // Url for clinical image upload. Only set if clinicalImageHasCrop is true for request dto
  clinicalImageUploadUrl?: string;

  // Id of created clinical training condition image. Only set if clinicalImageHasCrop is true for request dto
  clinicalTrainingConditionImageId?: string;
}
